import { AlertCircle, CheckCircle2, LoaderCircle } from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card"
import type { AlfredBulkImportUploadResult } from "@/types/alfred-bulk-import"

interface AlfredBulkImportStepProps {
  productName?: string
  fileName: string
  isSubmitting: boolean
  result: AlfredBulkImportUploadResult | null
  errorMessage: string | null
  onBack: () => void
  onRetry: () => void
}

export function AlfredBulkImportStep({
  productName = "Alfred",
  fileName,
  isSubmitting,
  result,
  errorMessage,
  onBack,
  onRetry,
}: AlfredBulkImportStepProps) {
  if (errorMessage) {
    return (
      <Card className="w-full max-w-3xl">
        <CardHeader>
          <h2 className="text-xl font-semibold text-foreground">Unable to start import</h2>
          <CardDescription>{fileName} was not accepted by the server.</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>Upload failed</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        </CardContent>
        <CardFooter className="flex flex-wrap justify-between gap-3">
          <Button type="button" variant="outline" onClick={onBack}>
            Back to preview
          </Button>
          <Button type="button" onClick={onRetry}>
            Retry upload
          </Button>
        </CardFooter>
      </Card>
    )
  }

  if (isSubmitting || !result) {
    return (
      <Card className="w-full max-w-3xl">
        <CardHeader>
          <h2 className="text-xl font-semibold text-foreground">Uploading devices</h2>
          <CardDescription>
            Sending {fileName} to {productName} for background processing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Keep this page open until the server accepts the file.
          </p>
        </CardContent>
        <CardFooter>
          <Button type="button" disabled>
            <LoaderCircle className="animate-spin" data-icon="inline-start" />
            Uploading CSV…
          </Button>
        </CardFooter>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-3xl">
      <CardHeader>
        <h2 className="text-xl font-semibold text-foreground">Import started</h2>
        <CardDescription>{fileName} was queued for background processing.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Alert>
          <CheckCircle2 />
          <AlertTitle>Job queued</AlertTitle>
          <AlertDescription className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">Job ID</span>
              <code className="break-all text-sm text-foreground">{result.jobId}</code>
            </div>
            <dl>
              <div className="flex flex-col gap-1">
                <dt className="text-xs text-muted-foreground">Rows queued</dt>
                <dd className="font-medium text-foreground">
                  {result.totalRows} row{result.totalRows === 1 ? "" : "s"}
                </dd>
              </div>
            </dl>
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  )
}
